import { Edge, MarkerType, Node, Position } from "@xyflow/react";
import { GraphLink } from "../models/GraphLink";
import InventoryItem from "../models/Inventory";
import { Invoice } from "../models/Invoice";

export const buildInventoryMap = (
    invoiceIds: string[],
    links: GraphLink[],
    inventory: InventoryItem[]
  ): Record<string, InventoryItem[]> => {
    const map: Record<string, InventoryItem[]> = {};
  
    links.forEach((link) => {
      const isFromInvoice = invoiceIds.includes(link.from);
      const invoiceId = isFromInvoice ? link.from : link.to;
      const inventoryId = isFromInvoice ? link.to : link.from;
      const item = inventory.find((inv) => inv.id === inventoryId);
  
      if (item) {
        if (!map[invoiceId]) map[invoiceId] = [];
        map[invoiceId].push(item);
      }
    });
  
    return map;
  };
  
  export const buildInvoiceAndInventoryNodesFlat = (
    invoices: Invoice[],
    inventoryMap: Record<string, InventoryItem[]>,
    batchId: string
  ) => {
    const nodes: Node[] = [];
    const edges: Edge[] = [];
  
    const x = -300;
    const spacingY = 50;
    let index = 0;
  
    invoices.forEach((invoice) => {
      const invoiceId = invoice.id;
      const baseY = index * spacingY;
  
      // 🧾 Visible invoice node (connects to batch)
      nodes.push({
        id: invoiceId,
        type: "invoice",
        data: { label: `${invoice.supplierName} ${invoice.invoiceNumber}` },
        position: { x, y: baseY },
        style: {
            width: 250,
          },
      });
  
      // 👻 Hidden node in the same position (connects to inventory)
      nodes.push({
        id: `${invoiceId}-inv`,
        data: { label: "" },
        position: { x, y: baseY },
        sourcePosition: Position.Bottom,
        style: {
          width: 250,
          opacity: 0,
        },
      });
  
      // ➡️ Edge to batch
      edges.push({
        id: `${invoiceId}-to-batch`,
        source: invoiceId,
        sourceHandle: "to-batch",
        target: `${batchId}-from-left`,
        type: "smoothstep",
        animated: true,
        markerEnd: { type: MarkerType.ArrowClosed, color: "#333" },
      });
  
      index++;
  
      const relatedInventory = Array.from(
        new Map(
          (inventoryMap[invoiceId] || []).map((inv) => [inv.id, inv])
        ).values()
      );
      
      relatedInventory.forEach((inv) => {
        const invY = index * spacingY;
  
        nodes.push({
          id: inv.id,
          data: { label: inv.name },
          position: { x, y: invY },
          targetPosition: Position.Top,
          style: {
            width: 250,
            backgroundColor: "#faff75",
            border: "1px solid #aaa",
            padding: 4,
          },
        });
  
        edges.push({
          id: `${invoiceId}-${inv.id}`,
          source: `${invoiceId}-inv`,
          target: inv.id,
          type: "smoothstep",
          animated: false,
          markerEnd: { type: MarkerType.ArrowClosed, color: "#888" },
        });
  
        index++;
      });
      index++; index++; index++;
    });
  
    const totalHeight = index * spacingY;
    return { invoiceAndInventoryNodes: nodes, invoiceAndInventoryEdges: edges, totalHeight };
  };
  
  
  
  export const buildBatchNodes = (batchId: string, label: string, y: number): Node[] => [
    {
      id: `${batchId}-from-left`,
      data: { label },
      position: { x: 0, y },
      sourcePosition: Position.Right,
      targetPosition: Position.Left,
      style: {
        backgroundColor: "#75cfff",
        fontWeight: 600,
        border: "2px solid #555",
        padding: 10,
      },
    },
    {
      id: `${batchId}-to-right`,
      data: { label },
      position: { x: 0, y },
      sourcePosition: Position.Right,
      targetPosition: Position.Left,
      style: {
        backgroundColor: "#75cfff",
        fontWeight: 600,
        border: "2px solid #555",
        padding: 10,
        opacity: 0,
        pointerEvents: "none",
      },
    },
  ];
  
  export const buildLossAndReleaseNodes = (batchId: string, centerY: number) => {
    const nodes: Node[] = [
      {
        id: `agp-loss`,
        data: { label: "Loss" },
        position: { x: 250, y: centerY - 100 },
        style: {
          backgroundColor: "#fdd",
          border: "2px solid #c00",
          padding: 8,
        },
        targetPosition: Position.Bottom,
      },
      {
        id: `agp-release`,
        data: { label: "Release" },
        position: { x: 250, y: centerY + 100 },
        style: {
          backgroundColor: "#dfd",
          border: "2px solid #0c0",
          padding: 8,
        },
        targetPosition: Position.Top,
      },
      {
        id: `${batchId}-agp-top`,
        data: { label: "AGP"},
        position: { x: 250, y: centerY },
        style: {
          backgroundColor: "#2ea597",
          border: "2px solid #2ea597",
          padding: 8,
        },
        targetPosition: Position.Left,
      },
      {
        id: `${batchId}-agp-bottom`,
        data: { label: "AGP"},
        position: { x: 250, y: centerY },
        style: {
          backgroundColor: "#2ea597",
          border: "2px solid #2ea597",
          padding: 8,
        },
      },
    ];
  
    const edges: Edge[] = [
      {
        id: `agp-to-loss`,
        source: `${batchId}-agp-top`,
        target: `agp-loss`,
        type: "smoothstep",
        animated: false,
        markerEnd: { type: MarkerType.ArrowClosed, color: "#c00" },        
      },
      {
        id: `agp-to-release`,
        source: `${batchId}-agp-bottom`,
        target: `agp-release`,
        type: "smoothstep",
        animated: false,
        markerEnd: { type: MarkerType.ArrowClosed, color: "#0c0" },
      },
      {
        id: `${batchId}-to-agp`,
        source: `${batchId}-to-right`,
        target: `${batchId}-agp-top`,
        type: "smoothstep",
        animated: false,
        markerEnd: { type: MarkerType.ArrowClosed, color: "#c00" },
      },
    ];
  
    return { nodes, edges };
  };
  